// src/pages/Devices.jsx
import React, { useEffect, useState, useCallback, useMemo, useContext } from "react";
import {
  Box,
  Paper,
  TablePagination,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";
import { request } from "../utils/request";
import { Link as RouterLink } from "react-router-dom";
import { ColorModeContext } from "../index";
import CommonDeleteModal from "../components/Common/CommonDeleteModal";

export default function Devices() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];

  const checkPerm = (action) => {
    if (isSuperAdmin) return true;
    return userPermissions.some((permission) => {
      const normalizedPermission = String(permission).toLowerCase();
      return (
        (normalizedPermission.includes('devices') || 
         normalizedPermission.includes('browser control')) &&
        normalizedPermission.includes(action.toLowerCase())
      );
    });
  };

  const canCreate = checkPerm('write');
  const canEdit = checkPerm('update');
  const canDelete = checkPerm('delete');

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const [page, setPage] = useState(0); 
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const normalizeDevice = (d) => {
    if (!d) return null;
    const id = d && (d._id || d.id) ? String(d._id || d.id) : null;
    return {
      _id: id,
      userId: d.userId || d.user_id || d.user || "",
      userName: d.userName || "",
      osVersion: d.osVersion || d.os_version || "",
      ip: d.ip || d.clientIp || "",
      gps: d.gps || "",
      macAddress: d.macAddress || d.mac_address || "",
      wifiSSID: d.wifiSSID || d.wifi_ssid || "",
      userPrivilege: d.userPrivilege || d.user_privilege || "",
      firewall: d.firewall || "",
      antivirus: d.antivirus || "",
      defenderRealtime: d.defenderRealtime || d.defender_realtime || "",
      bitlocker: d.bitlocker || "",
      secureBoot: d.secureBoot || "",
      tpm: d.tpm || "",
      createdAt: d.createdAt || d.created_at || null,
      raw: d,
    };
  };

  const extractDocsAndTotal = (resp) => {
    let docs = [];
    let total = 0;

    if (!resp) return { docs: [], total: 0 };

    if (Array.isArray(resp)) {
      docs = resp;
      total = docs.length;
      return { docs, total };
    }

    if (resp.success && resp.data) {
      const d = resp.data;
      if (Array.isArray(d.docs)) {
        docs = d.docs;
        total = Number(d.total ?? d.meta?.total ?? docs.length) || docs.length;
        return { docs, total };
      }
      if (Array.isArray(d)) {
        docs = d;
        total = docs.length;
        return { docs, total };
      }
    }

    if (resp.data) {
      const d = resp.data;
      if (Array.isArray(d.docs)) {
        docs = d.docs;
        total = Number(d.total ?? d.meta?.total ?? docs.length) || docs.length;
        return { docs, total };
      }
      if (Array.isArray(d)) {
        docs = d;
        total = docs.length;
        return { docs, total };
      }
    }

    if (Array.isArray(resp.docs)) {
      docs = resp.docs;
      total = Number(resp.total ?? resp.meta?.total ?? docs.length) || docs.length;
      return { docs, total };
    }

    const firstArray = Object.values(resp).find((v) => Array.isArray(v));
    if (firstArray) {
      docs = firstArray;
      total = docs.length;
      return { docs, total };
    }

    return { docs: [], total: 0 };
  };

  const fetchDevicesPage = useCallback(
    async (pageIndex = 0, limit = 25, search = "") => {
      setLoading(true);
      try {
        if (search) {
          try {
            const path = `devices?page=1&limit=1000&search=${encodeURIComponent(search)}`;
            const resp = await request(path, { method: "GET" });
            const { docs } = extractDocsAndTotal(resp);
            if (docs && docs.length > 0) {
              const normalized = docs.map(normalizeDevice).filter(Boolean);
              const t = search.toLowerCase();
              const filtered = normalized.filter((d) => {
                return (
                  (d.userId || "").toLowerCase().includes(t) ||
                  (d.userName || "").toLowerCase().includes(t) ||
                  (d.ip || "").toLowerCase().includes(t) ||
                  (d.macAddress || "").toLowerCase().includes(t) ||
                  (d.osVersion || "").toLowerCase().includes(t)
                );
              });
              setDevices(filtered.slice(0, 1000));
              setTotalRows(filtered.length);
              setLastFetchedAt(new Date());
              return;
            }
          } catch (err) {
            console.debug("Server search failed, falling back to client-side", err?.message || err);
          }

          const fallbackResp = await request(`devices?page=1&limit=1000`, { method: "GET" });
          const { docs } = extractDocsAndTotal(fallbackResp);
          const normalized = docs.map(normalizeDevice).filter(Boolean);
          const t = search.toLowerCase();
          const filtered = normalized.filter((d) => {
            return (
              (d.userId || "").toLowerCase().includes(t) ||
              (d.ip || "").toLowerCase().includes(t) ||
              (d.macAddress || "").toLowerCase().includes(t) ||
              (d.osVersion || "").toLowerCase().includes(t)
            );
          });
          setDevices(filtered.slice(0, 1000));
          setTotalRows(filtered.length);
          setLastFetchedAt(new Date());
          return;
        }

        const backendPage = Math.max(1, pageIndex + 1);
        const resp = await request(`devices?page=${backendPage}&limit=${limit}`, { method: "GET" });
        const { docs, total } = extractDocsAndTotal(resp);
        const normalized = docs.map(normalizeDevice).filter(Boolean);
        setDevices(normalized);
        setTotalRows(Number(total || normalized.length || 0));
        setLastFetchedAt(new Date());
      } catch (err) {
        console.error("Devices: fetch failed", err);
        setDevices([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (debouncedSearch) {
      setPage(0);
      fetchDevicesPage(0, rowsPerPage, debouncedSearch);
    } else {
      fetchDevicesPage(page, rowsPerPage, "");
    }
  }, [page, rowsPerPage, debouncedSearch, fetchDevicesPage]);

  const refresh = async () => {
    setLoading(true);
    try {
      await fetchDevicesPage(page, rowsPerPage, debouncedSearch);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (ev, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (ev) => {
    const n = parseInt(ev.target.value, 10);
    setRowsPerPage(n);
    setPage(0);
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return format(d, "yyyy-MM-dd HH:mm:ss");
    } catch {
      return String(iso);
    }
  };

  const statusChip = (d) => {
    const insecure = ["disabled", "off", "absent", "no", ""].includes(String(d.bitlocker).toLowerCase()) ||
      ["disabled", "off", "absent", "no", ""].includes(String(d.firewall).toLowerCase()) ||
      ["disabled", "off", "absent", "no", ""].includes(String(d.antivirus).toLowerCase());

    if (insecure) {
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: '6px', backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2', border: isDark ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid #fee2e2' }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: isDark ? '#f87171' : '#ef4444' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#f87171' : '#ef4444' }}>At Risk</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: '6px', backgroundColor: isDark ? 'rgba(34, 197, 94, 0.1)' : '#f0fdf4', border: isDark ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid #dcfce7' }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: isDark ? '#4ade80' : '#22c55e' }} />
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#4ade80' : '#22c55e' }}>Secure</Typography>
      </Box>
    );
  };

  const effectiveTotal = useMemo(() => Number(totalRows || devices.length || 0), [totalRows, devices]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = devices.length > 0 && selectedIds.length === devices.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(devices.map((d) => d._id));
    }
  };

  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const id = selectedIds[0];
    const dev = devices.find((d) => d._id === id);
    setEditFormData({
      _id: dev?._id || id,
      userId: dev?.userId || "",
      osVersion: dev?.osVersion || "",
      ip: dev?.ip || "",
      macAddress: dev?.macAddress || "",
      wifiSSID: dev?.wifiSSID || "",
    });
    setEditDialogOpen(true);
    setSelectedIds([]);
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => request(`devices/${id}`, { method: "DELETE" }))
      );
      setSelectedIds([]);
      await fetchDevicesPage(page, rowsPerPage, debouncedSearch);
      handleCancelBulkDelete();
    } catch (err) {
      console.error("Bulk delete failed:", err);
      window.alert("Failed to delete selected devices.");
      setBulkDeleting(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData || !editFormData._id) return;
    setEditLoading(true);
    try {
      const id = editFormData._id;
      await request(`devices/${id}`, { 
        method: "PUT", 
        body: {
          userId: editFormData.userId,
          osVersion: editFormData.osVersion,
          ip: editFormData.ip,
          macAddress: editFormData.macAddress,
          wifiSSID: editFormData.wifiSSID,
        }
      });
      await fetchDevicesPage(page, rowsPerPage, debouncedSearch);
      setEditDialogOpen(false);
      setEditFormData(null);
    } catch (err) {
      console.error("Device update failed:", err);
      window.alert("Failed to update device.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditFormData(null);
    setEditLoading(false);
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
          <RouterLink to="/home" className="global-link" style={{ textDecoration: "none" }}>
            Dashboard
          </RouterLink>
          <Typography className="global-section-title" sx={{ fontSize: '0.95rem', margin: 0 }}>
            Devices
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: "1 1 auto", justifyContent: "flex-end" }}>
          <TextField
            size="small"
            placeholder="Search by User ID, IP, MAC, OS"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" className="text-secondary" />
                </InputAdornment>
              ),
            }}
            className="global-input"
            sx={{ minWidth: 260 }}
          />

          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh} className="global-icon-button">
              <RefreshIcon fontSize="small" className="text-primary" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {selectedIds.length > 0 && (
        <Box mb={2} display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
          <Typography variant="body2" className="text-primary">
            {selectedIds.length} selected
          </Typography>

          {canEdit && (
            <Button
              size="small"
              className="global-button global-button-outlined"
              onClick={handleEditSelected}
              disabled={selectedIds.length !== 1}
            >
              Edit
            </Button>
          )}

          {canDelete && (
            <Button
              size="small"
              className="global-button global-button-danger"
              onClick={handleDeleteSelected}
            >
              Delete
            </Button>
          )}

          <Button
            size="small"
            className="global-button global-button-text"
            onClick={() => setSelectedIds([])}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Main Container */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 2,
          overflow: 'hidden',
          borderRadius: '12px',
          backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
          border: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB',
          boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
      >
        <TableContainer 
          sx={{ 
            maxHeight: 'calc(100vh - 230px)',
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? 'rgba(255, 255, 255, 0.2) transparent' : 'rgba(0, 0, 0, 0.2) transparent',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)', borderRadius: '10px' },
            '&::-webkit-scrollbar-thumb:hover': { background: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)' }
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1200, borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  padding="checkbox" 
                  sx={{ 
                    backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                    borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'select all devices' }}
                    sx={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}
                  />
                </TableCell>
                {[
                  { label: '#', width: '40px' },
                  { label: 'User ID', width: '150px' },
                  { label: 'User Name', width: '200px' },
                  { label: 'OS', width: '150px' },
                  { label: 'IP', width: '120px' },
                  { label: 'MAC', width: '150px' },
                  { label: 'Wi-Fi', width: '120px' },
                  { label: 'Privilege', width: '120px' },
                  { label: 'Posture', width: '120px' },
                  { label: 'Created', width: '150px' }
                ].map((col, idx) => (
                  <TableCell 
                    key={idx} 
                    sx={{ 
                      width: col.width,
                      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                      borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB',
                      color: isDark ? '#9CA3AF' : '#6B7280',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      py: 2
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, color: isDark ? '#9CA3AF' : '#6B7280', borderBottom: 'none' }}>
                    No devices found.
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((d, idx) => {
                  const isSelected = selectedIds.includes(d._id);
                  return (
                    <TableRow 
                      key={d._id || `${d.macAddress}-${idx}`}
                      hover
                      selected={isSelected}
                      sx={{ 
                        '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.02) !important' : '#F9FAFB !important' },
                        '&.Mui-selected': { backgroundColor: isDark ? 'rgba(59, 130, 246, 0.08) !important' : '#EFF6FF !important' },
                        '&.Mui-selected:hover': { backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12) !important' : '#DBEAFE !important' }
                      }}
                    >
                      <TableCell 
                        padding="checkbox"
                        sx={{ 
                          borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB',
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => toggleSelect(d._id)}
                          inputProps={{ 'aria-label': `select device ${d._id || d.macAddress}` }}
                          sx={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}
                        />
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', color: isDark ? '#D1D5DB' : '#4B5563', fontSize: '0.875rem' }}>
                        {page * rowsPerPage + idx + 1}
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', wordBreak: 'break-all' }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: isDark ? '#F9FAFB' : '#111827' }}>
                          {d.userId || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', color: isDark ? '#D1D5DB' : '#4B5563', fontSize: '0.875rem' }}>
                        {d.userName || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', color: isDark ? '#D1D5DB' : '#4B5563', fontSize: '0.875rem', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                        {d.osVersion || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', color: isDark ? '#D1D5DB' : '#4B5563', fontSize: '0.875rem' }}>
                        {d.ip || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', color: isDark ? '#D1D5DB' : '#4B5563', fontSize: '0.875rem' }}>
                        {d.macAddress || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', color: isDark ? '#D1D5DB' : '#4B5563', fontSize: '0.875rem' }}>
                        {d.wifiSSID || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', color: isDark ? '#D1D5DB' : '#4B5563', fontSize: '0.875rem' }}>
                        {d.userPrivilege || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB' }}>
                        {statusChip(d)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', color: isDark ? '#9CA3AF' : '#6B7280', fontSize: '0.875rem' }}>
                        {fmt(d.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination at the bottom */}
        <Box sx={{ 
          borderTop: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB', 
          backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px'
        }}>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={effectiveTotal}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page"
            sx={{
              color: isDark ? '#D1D5DB' : '#4B5563',
              '& .MuiTablePagination-selectIcon': { color: isDark ? '#D1D5DB' : '#4B5563' },
              '& .MuiIconButton-root.Mui-disabled': { opacity: 0.3 }
            }}
          />
        </Box>
      </Paper>

      <CommonDeleteModal
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        isDeleting={bulkDeleting}
        entityType="Device"
        isBulk={true}
        bulkCount={selectedIds.length}
      />

      <Dialog 
        open={editDialogOpen} 
        onClose={handleEditCancel} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{ className: 'global-card' }}
      >
        <form onSubmit={handleEditSubmit}>
          <DialogTitle className="global-dialog-title">
            Edit Device
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'var(--hairline)', p: 3 }}>
            {editFormData ? (
              <Stack spacing={2.5}>
                <TextField
                  label="User ID"
                  name="device_custom_userid"
                  autoComplete="off"
                  value={editFormData.userId}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, userId: e.target.value }))}
                  fullWidth
                  className="global-input"
                  size="small"
                />
                <TextField
                  label="OS Version"
                  name="device_custom_osversion"
                  autoComplete="off"
                  value={editFormData.osVersion}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, osVersion: e.target.value }))}
                  fullWidth
                  className="global-input"
                  size="small"
                />
                <TextField
                  label="IP Address"
                  name="device_custom_ip"
                  autoComplete="off"
                  value={editFormData.ip}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, ip: e.target.value }))}
                  fullWidth
                  className="global-input"
                  size="small"
                />
                <TextField
                  label="MAC Address"
                  name="device_custom_mac"
                  autoComplete="off"
                  value={editFormData.macAddress}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, macAddress: e.target.value }))}
                  fullWidth
                  className="global-input"
                  size="small"
                />
                <TextField
                  label="Wi-Fi SSID"
                  name="device_custom_wifi"
                  autoComplete="off"
                  value={editFormData.wifiSSID}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, wifiSSID: e.target.value }))}
                  fullWidth
                  className="global-input"
                  size="small"
                />
              </Stack>
            ) : (
              <Typography className="text-secondary">Loading…</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: "1px solid var(--hairline)" }}>
            <Button 
              onClick={handleEditCancel} 
              disabled={editLoading} 
              className="global-button-text"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={editLoading} 
              variant="contained" 
              className="global-button global-button-primary"
            >
              {editLoading ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}